import React, { useCallback, useRef, useState } from 'react';
import { Box, CircularProgress, Dialog } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { TOKENS } from '../board/ganttConfig';
import {
  InkForecastImportResponse, InkForecastImportResult, importInkForecasts,
} from '../productionApi';

/**
 * Drop Prism Raw Material forecast PDFs to load a projection.
 *
 * Until this existed a projection could only be loaded by running
 * scripts/import_rm_forecast.py on a machine with the repo checked out, which
 * made the report unusable by the people who actually receive the PDFs. The
 * script remains the right tool for bulk and for --dry-run; this is the same
 * import (helpers/rm_forecast_import) reached over HTTP.
 *
 * WHY THE RESULT LIST IS THE POINT, NOT THE PROGRESS BAR
 *
 * A forecast PDF can parse perfectly and still disagree with Prism's own printed
 * roll-up — eight of ten real projections tie exactly, the other two land at 95%
 * and 98%. Those are imported and FLAGGED rather than rejected, because a 97.6%
 * projection is far more useful than none. That decision is only defensible if
 * the operator is told, so every file reports its tie and its single worst cell
 * instead of a bare "uploaded". A silent success would be the dangerous outcome.
 */

const MONO = TOKENS.monoFont;
const RULE = TOKENS.line;

function isPdf(file: File) {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

function fmt(n: number | undefined) {
  return typeof n === 'number' ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—';
}

function ResultRow({ r }: { r: InkForecastImportResult }) {
  const tie = r.tie;
  // A tie below 100% is worth the eye, but it is NOT an error — colour it as a
  // caveat, not a failure, or operators learn to ignore genuine failures.
  const tieColor = !tie?.pct ? TOKENS.ink3 : tie.clean ? TOKENS.good : TOKENS.warn;
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: '2px',
      px: 1.25, py: '7px', borderBottom: `1px solid ${RULE}`,
      borderLeft: `3px solid ${r.ok ? (tie?.clean ? TOKENS.good : TOKENS.warn) : TOKENS.bad}`,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
        <Box component="span" sx={{ fontSize: 12, fontWeight: 700 }}>
          {r.ok ? `${r.customer} ${r.projection}` : r.filename}
        </Box>
        {r.ok && (
          <Box component="span" sx={{ fontSize: 11, color: TOKENS.ink3 }}>
            {r.filename}
          </Box>
        )}
        {r.replaced && (
          <Box component="span" sx={{
            fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
            color: TOKENS.ink2, border: `1px solid ${RULE}`, borderRadius: '2px', px: '4px',
          }}>
            replaced
          </Box>
        )}
      </Box>

      {r.ok ? (
        <Box sx={{
          display: 'flex', gap: '14px', flexWrap: 'wrap',
          fontFamily: MONO, fontSize: 11.5, color: TOKENS.ink2,
          fontVariantNumeric: 'tabular-nums',
        }}>
          <span>{fmt(r.lines)} lines ({fmt(r.lines_with_demand)} with demand)</span>
          <span>{fmt(r.materials)} boards</span>
          <span>{fmt(r.total_sheets)} sheets</span>
          {tie && tie.cells > 0 && (
            <Box component="span" sx={{ color: tieColor, fontWeight: 700 }}>
              tie {tie.exact}/{tie.cells} ({tie.pct}%)
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ fontSize: 11.5, color: TOKENS.bad }}>{r.error}</Box>
      )}

      {/* The one cell that disagrees most. Naming it turns "97.6%" from an
          abstract score into something a person can go and check in Prism. */}
      {r.ok && tie?.worst && (
        <Box sx={{ fontFamily: MONO, fontSize: 11, color: TOKENS.ink3 }}>
          largest gap {tie.worst.rm_code} {tie.worst.month.slice(0, 3)} — ours{' '}
          {fmt(tie.worst.ours)} vs Prism {fmt(tie.worst.prism)} ({tie.worst.diff > 0 ? '+' : ''}
          {fmt(tie.worst.diff)})
        </Box>
      )}
    </Box>
  );
}

export function ForecastDropZone({ compact = false }: { compact?: boolean }) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [response, setResponse] = useState<InkForecastImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [skipped, setSkipped] = useState<string[]>([]);

  const upload = useCallback(async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const all = Array.from(fileList);
    const pdfs = all.filter(isPdf);
    // Named rather than silently dropped: someone who drags a whole folder should
    // be told what was ignored, not left wondering why 7 of 9 landed.
    setSkipped(all.filter((f) => !isPdf(f)).map((f) => f.name));
    if (pdfs.length === 0) {
      setError('No PDFs in that drop — Prism forecasts are PDF files.');
      setResponse(null);
      return;
    }
    setBusy(true);
    setError(null);
    setResponse(null);
    try {
      const res = await importInkForecasts(pdfs);
      setResponse(res);
      // Refetch the report and the projection picker. Broad key on purpose: an
      // import can add a projection the current selection does not mention, and
      // the picker must see it immediately or the new data looks like it vanished.
      if (res.imported > 0) {
        queryClient.invalidateQueries({ queryKey: ['production', 'ink-forecast'] });
      }
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Upload failed.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [queryClient]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0 }}>
      <Box
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          upload(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        sx={{
          border: `1.5px dashed ${dragging ? TOKENS.accent : RULE}`,
          bgcolor: dragging ? 'rgba(203,96,21,0.06)' : 'transparent',
          borderRadius: '3px',
          px: 2, py: compact ? 2 : 3.5,
          textAlign: 'center', cursor: busy ? 'default' : 'pointer',
          transition: 'border-color .12s, background-color .12s',
          '&:hover': { borderColor: busy ? RULE : TOKENS.accent },
        }}
      >
        {busy ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25 }}>
            <CircularProgress size={16} sx={{ color: TOKENS.accent }} />
            <Box component="span" sx={{ fontSize: 12.5, color: TOKENS.ink2 }}>
              Parsing…
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{ fontSize: 13, fontWeight: 700, mb: '3px' }}>
              Drop Prism Raw Material forecast PDFs here
            </Box>
            <Box sx={{ fontSize: 11.5, color: TOKENS.ink3 }}>
              or click to choose files · several at once is fine · re-dropping a
              projection replaces it
            </Box>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          hidden
          onChange={(e) => upload(e.target.files)}
        />
      </Box>

      {error && (
        <Box sx={{
          fontSize: 12, color: TOKENS.bad,
          borderLeft: `3px solid ${TOKENS.bad}`, pl: 1, py: '4px',
        }}>
          {error}
        </Box>
      )}

      {skipped.length > 0 && (
        <Box sx={{ fontSize: 11.5, color: TOKENS.ink3 }}>
          Ignored {skipped.length} non-PDF file{skipped.length > 1 ? 's' : ''}: {skipped.join(', ')}
        </Box>
      )}

      {response && (
        <Box sx={{ border: `1px solid ${RULE}`, borderRadius: '3px', overflow: 'hidden' }}>
          <Box sx={{
            px: 1.25, py: '6px', bgcolor: 'rgba(0,0,0,0.02)',
            borderBottom: `1px solid ${RULE}`, fontSize: 11.5, color: TOKENS.ink2,
          }}>
            <b>{response.imported}</b> projection{response.imported === 1 ? '' : 's'} imported
            {response.failed > 0 && <> · <b>{response.failed}</b> failed</>}
          </Box>
          {response.results.map((r, i) => <ResultRow key={`${r.filename}-${i}`} r={r} />)}
        </Box>
      )}
    </Box>
  );
}

/**
 * The same drop zone behind a control-bar button, for when a forecast is already
 * on screen. The empty state embeds the zone directly instead — with nothing
 * loaded, importing is the only thing a person can usefully do, so it should not
 * be hidden behind a click.
 */
export function ForecastImportButton({ ButtonComponent }: {
  ButtonComponent: React.ComponentType<{ label: string; onClick: () => void; title?: string }>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <ButtonComponent
        label="Import PDFs"
        onClick={() => setOpen(true)}
        title="Load a Prism Raw Material forecast PDF"
      />
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <Box sx={{ p: 2, bgcolor: TOKENS.bg }}>
          <Box sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>
            Import raw-material forecast
          </Box>
          <ForecastDropZone compact />
        </Box>
      </Dialog>
    </>
  );
}

export default ForecastDropZone;
